import React from "react";
import { TrendingUp, TrendingDown, Store as StoreIcon, ChevronRight, ShoppingCart, ShoppingBag } from "lucide-react";
import { fmtINR, fmtINRCompact } from "../utils";

// Two big KPI cards (Sales / Purchase) plus a small store-count tile and
// an invoice-count tile. Clicking either large card drills into the
// Category level.
export default function OverallCards({ totals, onDrill }) {
  const {
    sales,
    sales_bills,
    sales_returns,
    sales_returns_count,
    purchase,
    store_count,
    sales_count,
    purchase_count,
    sales_qty,
    purchase_qty,
  } = totals;
  const net = sales - purchase;
  const netUp = net >= 0;
  // Caption shown under Sales when returns were deducted, so the cashier
  // sees both the gross bills and the net (which is bills − returns).
  const salesCaption =
    Number(sales_returns) > 0
      ? `${sales_count} bill${sales_count === 1 ? "" : "s"} · ₹${Math.round(
        sales_bills
      ).toLocaleString("en-IN")} gross − ₹${Math.round(sales_returns).toLocaleString(
        "en-IN"
      )} return${sales_returns_count === 1 ? "" : "s"}`
      : `${sales_count} bill${sales_count === 1 ? "" : "s"}`;
  // Strip-level quantity (net sold / received) rides along the count caption
  // and stays consistent with whatever filters (dates, scopes, item) applied.
  const qtyCaption = (n) =>
    Number.isFinite(Number(n)) ? ` · ${Number(n).toLocaleString("en-IN")} strips` : "";
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <BigCard
        kind="sales"
        amount={sales}
        countLabel={salesCaption + qtyCaption(sales_qty)}
        helpLabel="Net sales = Bills − Returns, selected period"
        Icon={ShoppingCart}
        onDrill={onDrill}
      />
      <BigCard
        kind="purchase"
        amount={purchase}
        countLabel={`${purchase_count} GRN${purchase_count === 1 ? "" : "s"}` + qtyCaption(purchase_qty)}
        helpLabel="Goods received at stores, selected period"
        Icon={ShoppingBag}
        onDrill={onDrill}
      />
      <SmallCard
        icon={<StoreIcon size={16} />}
        label="Stores in scope"
        value={String(store_count)}
        accent="text-[var(--dashboard-gray-900)]"
      />
      <SmallCard
        icon={netUp ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
        label={netUp ? "Net (Sales − Purchase)" : "Net (Purchase exceeds Sales)"}
        value={(netUp ? "" : "−") + fmtINRCompact(Math.abs(net))}
        accent={netUp ? "text-[var(--dashboard-success)]" : "text-[var(--dashboard-danger)]"}
      />
    </div>
  );
}

function BigCard({ kind, amount, countLabel, helpLabel, Icon, onDrill }) {
  const tone =
    kind === "sales"
      ? {
        pillBg: "bg-[var(--dashboard-bg-success)]",
        pillText: "text-[var(--dashboard-green-dark)]",
        textAccent: "text-[var(--dashboard-green-dark)]",
        ring: "hover:ring-2 hover:ring-[var(--dashboard-green-dark)]/15",
      }
      : {
        pillBg: "bg-[var(--dashboard-bg-warning)]",
        pillText: "text-[var(--dashboard-warning-dark)]",
        textAccent: "text-[var(--dashboard-warning-dark)]",
        ring: "hover:ring-2 hover:ring-[var(--dashboard-warning-dark)]/15",
      };
  return (
    <button
      type="button"
      onClick={() => onDrill && onDrill(kind)}
      className={`text-left bg-white border border-[var(--dashboard-gray-200)] rounded-xl shadow-sm px-6 py-5 transition hover:-translate-y-0.5 hover:shadow-md ${tone.ring}`}
    >
      <div className="flex items-center justify-between mb-3">
        <span
          className={`inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${tone.pillBg} ${tone.pillText}`}
        >
          <Icon size={11} />
          {kind === "sales" ? "Sales" : "Purchase"}
        </span>
        <span className="text-[12px] text-[var(--dashboard-gray-400)] flex items-center gap-1">
          View by category <ChevronRight size={12} />
        </span>
      </div>
      <div className={`font-mono text-[32px] font-bold tracking-tight ${tone.textAccent}`}>
        {fmtINR(amount)}
      </div>
      <div className="text-[12.5px] text-[var(--dashboard-gray-500)] mt-3 flex items-center justify-between">
        <span>{helpLabel}</span>
        <span className="font-mono">{countLabel}</span>
      </div>
    </button>
  );
}

function SmallCard({ icon, label, value, accent }) {
  return (
    <div className="bg-white border border-[var(--dashboard-gray-200)] rounded-xl shadow-sm px-5 py-4 flex items-center gap-4">
      <div className="w-10 h-10 rounded-lg bg-[var(--nav-head-bg)] text-[var(--dashboard-gray-500)] flex items-center justify-center">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10.5px] font-bold uppercase tracking-wide text-[var(--dashboard-gray-500)]">
          {label}
        </div>
        <div className={`font-mono text-[18px] font-bold mt-0.5 ${accent}`}>{value}</div>
      </div>
    </div>
  );
}
